// ─── Household / Auth (multi-tenant ownership) ──────────────────

// Roles a user can hold in a household. `as const` so there are no
// scattered string literals — reference HOUSEHOLD_ROLES / Role everywhere.
export const HOUSEHOLD_ROLES = ["owner", "member"] as const;
export type Role = (typeof HOUSEHOLD_ROLES)[number];

// The session user resolved from the JWT + membership. householdId/role are
// nullable during the per-user → per-household transition (a user created
// before the migration has no membership yet).
export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  householdId: string | null;
  role: Role | null;
}

export interface CSVMapping {
  id: string;
  filePattern: string;
  columnMappings: Record<string, StandardField>;
  dateFormat: string;
}

export type StandardField =
  | "date"
  | "description"
  | "amount"
  | "type"
  | "balance"
  | "checkNumber"
  | "details"
  | "counterparty"
  | "fee"
  | "status"
  | "externalId"
  | "sourceAccount"
  | "currency"
  | "note"
  | "ignore";

export const STANDARD_FIELDS: { value: StandardField; label: string }[] = [
  { value: "date", label: "Date" },
  { value: "description", label: "Description" },
  { value: "amount", label: "Amount" },
  { value: "type", label: "Type" },
  { value: "balance", label: "Balance" },
  { value: "details", label: "Details (Debit/Credit)" },
  { value: "checkNumber", label: "Check Number" },
  { value: "counterparty", label: "Counterparty (Sender/Receiver)" },
  { value: "fee", label: "Fee" },
  { value: "status", label: "Status" },
  { value: "externalId", label: "Transaction ID" },
  { value: "sourceAccount", label: "Source Account" },
  { value: "currency", label: "Currency" },
  { value: "note", label: "Note / Memo" },
  { value: "ignore", label: "— Ignore —" },
];

// Family / household members
export interface FamilyMember {
  id: string;
  name: string;              // "Mark", "Mel"
  role: "owner" | "member";  // Household role
  color: string;             // Badge color for UI
  knownAccounts?: string[];  // sourceAccount values that belong to this person
                             // e.g. ["Visa Debit 9491", "Cash Balance"]
}

export interface CSVSummary {
  totalTransactions: number;
  totalSpent: number;
  totalReceived: number;
  averageTransaction: number;
  dateRange: string;
  transferCount: number;
  transferVolume: number;
}

// Per-account money breakdown — "who spent what, from whose account" (#172 Stage 2).
// Debt repayment is carried SEPARATELY rather than folded into `spent`: it was
// previously counted as spending, so hiding it would silently shrink a total
// with no explanation of why.
export interface AccountSpend {
  accountId: string;
  name: string;
  spent: number;          // real spending — excludes transfers and loan principal
  received: number;       // real money in — same exclusions
  debtRepaid: number;     // loan principal repaid: not spending, but not nothing
  transferredOut: number; // moved to another account you own
  txCount: number;        // transactions counted in `spent` + `received`
}

// Account identity - who owns what
export interface AccountMeta {
  accountId: string;
  owner: string;              // "Mark", "Mel"
  institution: string;        // "Cash App", "Chase", "Bank of America"
  accountNumber?: string;     // Last 4 or full - for matching/deduping
  accountType: string;        // "checking", "cash-app", "savings", "credit-card"
  friendlyName?: string;      // "Mark's Cash App", "Mel's Chase Checking"
}

// Business entities
export interface BusinessEntity {
  id: string;
  name: string;               // "AIRE", "LHB", "Personal"
  fullName: string;           // "AI Real Estate Investor LLC"
  type: "llc" | "sole-prop" | "personal";
  owner: string;              // "Mark"
  color: string;              // For UI badges
  /** Transactions linked to this row — from the FK relation (#176). */
  linkedCount?: number;
}

// Expense classes within an entity
export interface ExpenseClass {
  id: string;
  entityId: string;
  name: string;               // "Software", "Marketing", "Office"
  description?: string;
}

// Default expense categories per entity type
export const DEFAULT_CATEGORIES: Record<string, string[]> = {
  personal: [
    "Income", "Debt", "Bills & Utilities", "Groceries", "Dining",
    "Gas & Auto", "Shopping", "Entertainment", "Health", "Subscriptions", "Other",
  ],
  aire: [
    "Income", "Software", "Marketing", "Office", "Contractors", "Other",
  ],
  lhb: [
    "Income", "Materials", "Insurance", "Contractors", "Permits", "Other",
  ],
};

// Classification rules - remembered entity assignments by transaction name
export interface ClassificationRule {
  id: string;
  pattern: string;            // Transaction name (exact or normalized)
  entityId: string;           // Which entity to assign
  expenseCategory?: string;   // Which category within the entity
  incomeSourceId?: string;    // Which income source (for credit transactions)
  isTransfer?: boolean;       // Should this be marked as transfer?
  createdAt: string;          // ISO date
}

// Budget plan - spending targets (NOT actual transactions)
export interface BudgetPlanLine {
  id: string;
  category: string;           // "Rent", "Groceries", "T-Mobile"
  budgeted: number;           // Monthly target amount
  entityId?: string;          // Links to BusinessEntity. undefined = Personal
}

export interface StoredBudgetPlan {
  id: string;
  name: string;               // "2026 Out of Debt Plan"
  totalIncome: number;        // Expected monthly income
  lines: BudgetPlanLine[];
  isActive: boolean;
  createdAt: string;
}

// Detected recurring subscriptions
export interface DetectedSubscription {
  id: string;
  merchantName: string;
  expenseCategory: string;
  entityId?: string;
  averageAmount: number;
  frequency: "monthly" | "quarterly" | "yearly" | "weekly";
  lastChargeDate: string;
  nextExpectedDate: string;
  monthsDetected: number;
  status: "active" | "cancelled" | "new";
  transactions: Transaction[];
  confidence: number;            // 0-1
  source: "plaid" | "csv";
}

// Income sources
export interface IncomeSource {
  id: string;
  name: string;               // "Pete (IHB)", "Mel's Paycheck"
  owner: string;              // "Mark", "Mel"
  friendlyName?: string;      // "Pete" for IHB
  type: "salary" | "commission" | "transfer" | "business" | "other";
  frequency: "weekly" | "biweekly" | "monthly" | "irregular";
  expectedAmount?: number;
  notes?: string;
  /** Transactions linked to this row — from the FK relation (#176). */
  linkedCount?: number;
  /** What to look for inside a transaction description; falls back to name (#176). */
  matchPattern?: string;
}
